import React, { useState, useEffect, useContext } from "react";
import AppContext from "../../context/AppContext.js";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/avs.png";
import { useAuth } from "../../context/AuthContext.js";

import "./Sidebar.css";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasAccess, isAdmin, isSuperAdmin } = useAuth();

  const { isMobileMenuOpen, setIsMobileMenuOpen, setLoading } = useContext(AppContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [popupTop, setPopupTop] = useState(0);
  const [popupLeft, setPopupLeft] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
      if (window.innerWidth > 1024) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setIsMobileMenuOpen]);



  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobile && isMobileMenuOpen) {
        const sidebar = document.querySelector(".sidebar");
        const hamburger = document.querySelector(".mobile-hamburger");

        if (
          sidebar &&
          !sidebar.contains(event.target) &&
          hamburger &&
          !hamburger.contains(event.target)
        ) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile, isMobileMenuOpen, setIsMobileMenuOpen]);

  const handleNavigation = (path) => {
    if (location.pathname === path) {
      if (isMobile) setIsMobileMenuOpen(false);
      return;
    }
    setLoading(true);
    setTimeout(() => {
      navigate(path);
      if (isMobile) {
        setIsMobileMenuOpen(false);
      }
      setTimeout(() => setLoading(false), 500);
    }, 300);
  };

  const handleLogoClick = () => {
    navigate("/dashboard");
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // ===== NAVIGATION ITEMS WITH CORRECT PATHS =====
  const navItems = [
    { icon: "dashboard", label: "Dashboard", path: "/dashboard", module: "dashboard" },
    {
      icon: "inventory_2",
      label: "Stock",
      path: "/stock",
      module: "stock",
      children: [
        { label: "Finished Goods", path: "/stock" },
        { label: "Raw Materials", path: "/stock/raw-materials" }
      ]
    },
    { icon: "format_list_bulleted", label: "Products", path: "/products", module: "products" },

    // ✅ PRODUCTION - SUB-MENU
    {
      icon: "factory",
      label: "Production",
      path: "/production",
      module: "production",
      children: [
        { label: "Daily Production", path: "/production/daily" },
        { label: "Production Plan", path: "/production/plan" }
      ]
    },

    {
      icon: "sell",
      label: "Sales",
      path: "/sales",
      module: "sales",
      children: [
        { label: "Sale", path: "/sales" },
        { label: "Order Details", path: "/sales/history" }
      ]
    },
    {
      icon: "payments",
      label: "Expenses",
      path: "/expenses",
      module: "expenses",
      children: [
        { label: "Daily Expenses", path: "/expenses" },
        { label: "Expense Report", path: "/expenses/report" },
        { label: "Salary Report", path: "/salary", module: "salary" }
      ]
    },
    { icon: "badge", label: "Employees", path: "/employees", module: "employees" },
    { icon: "group", label: "Clients", path: "/clients", module: "clients" },
    { icon: "store", label: "Vendors", path: "/vendors", module: "vendors" },

    // ATTENDANCE SUB-MENU (Admin View)
    {
      icon: "event_available",
      label: "Attendance (Admin)",
      path: "/attendance",
      module: "attendance",
      isSubmenu: true,
      children: [
        { label: "Daily Log", path: "/attendance" },
        { label: "Attendance Report", path: "/attendance-report" }
      ]
    },
    // MY ATTENDANCE (Employee View)
    {
      icon: "fingerprint",
      label: "My Attendance",
      path: "/my-attendance",
      hideForAdmin: true,
      alwaysShow: !isAdmin
    },
    { icon: "analytics", label: "Turnover", path: "/turnover", module: "turnover" },
    { icon: "notifications", label: "Notifications", path: "/notifications", module: "notifications" },
  ];

  // Filter items based on access and administrative exposure
  const filteredNavItems = navItems.filter(item => {
    if (item.hideForAdmin && isAdmin) return false;
    if (item.alwaysShow) return true;
    
    // Check if the primary module has access
    if (item.module && hasAccess(item.module)) return true;
    
    // If it has children, check if the user has access to any of the children's specific modules
    if (item.children && item.children.length > 0) {
      return item.children.some(sub => sub.module && hasAccess(sub.module));
    }
    
    return false;
  });

  // Add Admin-specific modules
  if (isSuperAdmin) {
    filteredNavItems.push({
      icon: "campaign",
      label: "Push Alerts",
      path: "/admin/push-notifications",
      module: "admin"
    });
    
    filteredNavItems.push({
      icon: "admin_panel_settings",
      label: "Manage Access",
      path: "/admin/manage-access",
      module: "admin"
    });
  }

  // navItems already includes Notifications now

  // Add Check Status for all (it's a system utility)
  filteredNavItems.push({
    icon: "check_circle",
    label: "Status",
    path: "/check",
    apiStatus: true // Green dot indicator
  });


  const [expandedMenu, setExpandedMenu] = useState(null);

  const toggleSubMenu = (item) => {
    if (item.children && item.children.length > 0) {
      // On mobile: just expand/collapse the submenu — do NOT navigate
      setExpandedMenu(prev => prev === item.label ? null : item.label);
    } else {
      // No children: navigate directly
      handleNavigation(item.path);
    }
  };

  return (
    <>

      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-overlay active"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      <aside
        className={`sidebar ${isMobile ? "mobile" : ""} ${isMobileMenuOpen ? "mobile-open" : ""}`}
      >
        <div className="sidebar-header">
          <div className="logo-container logo-container-mobile-fix" onClick={handleLogoClick}>
            <img src={logo} alt="AVSECO Logo" className="logo-full logo-mobile-enhanced" />
          </div>
          {isMobile && (
            <button className="mobile-close" onClick={() => setIsMobileMenuOpen(false)}>
              <span className="material-symbols-outlined">close</span>
            </button>
          )}
        </div>


        <nav className="sidebar-nav">
          {filteredNavItems.map((item) => (
            <React.Fragment key={item.label}>
              {item.children ? (
                // Parent Item with Submenu (Hover/Popup)
                <div
                  className={`nav-group popup-group ${isActive(item.path) || location.pathname.includes(item.path) ? "active-group" : ""}`}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      setExpandedMenu(item.label);
                      const rect = e.currentTarget.getBoundingClientRect();
                      setPopupTop(rect.top);
                      setPopupLeft(rect.right);
                    }
                  }}
                  onMouseLeave={() => !isMobile && setExpandedMenu(null)}
                >
                  <div
                    className={`nav-item ${isActive(item.path) || location.pathname.includes(item.path) ? "active" : ""}`}
                    onClick={() => toggleSubMenu(item)}
                  >
                    <span className="material-symbols-outlined">{item.icon}</span>
                    <p className="nav-text">{item.label}</p>
                    <span className="material-symbols-outlined branch-icon">
                      {isMobile ? (expandedMenu === item.label ? "expand_less" : "expand_more") : "chevron_right"}
                    </span>
                  </div>

                  {/* Submenu Logic */}
                  {(expandedMenu === item.label) && (
                    <div
                      className={`submenu ${!isMobile ? 'popup-menu' : ''}`}
                      style={!isMobile ? { top: popupTop, left: popupLeft, position: 'fixed', marginLeft: 0 } : {}}
                    >
                      {item.children
                        .filter(sub => {
                          const requiredModule = sub.module || item.module;
                          return hasAccess(requiredModule);
                        })
                        .map((sub) => (
                          <div
                            key={sub.label}
                            className={`submenu-item ${isActive(sub.path) ? "active" : ""}`}
                            onClick={() => handleNavigation(sub.path)}
                          >
                            <span className="submenu-text">{sub.label}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                // Regular Item
                <div
                  className={`nav-item ${isActive(item.path) ? "active" : ""}`}
                  onClick={() => handleNavigation(item.path)}
                >
                  <span className="material-symbols-outlined">{item.icon}</span>
                  <p className="nav-text">{item.label}</p>

                  {/* API Status Indicator */}
                  {item.apiStatus !== undefined && (
                    <span
                      className={`api-status-dot ${item.apiStatus ? "connected" : "disconnected"}`}
                      title={item.apiStatus ? "API Connected" : "API Error"}
                    ></span>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}


        </nav>

      </aside >
    </>
  );
};

export default Sidebar;